import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { conditionColor } from '@redevicer/shared'
import { listingsAPI } from '../api/client'
import OfferModal from './OfferModal'
import { blocksOwnListingAction } from '../utils/testDeals'
import { useFavorites } from '../hooks/useFavorites'
import ListingFavoriteButton from './ListingFavoriteButton'
import { IconHeart } from './AppNavIcons'
import { conditionLabel as formatConditionLabel } from '../utils/conditionLabel'
import { listingCardPhotoUrls } from '../utils/listingThumb'
import ListingCardPhotoCarousel from './ListingCardPhotoCarousel'
import ListingCardSoldOverlay, { listingCardSoldCoverClass } from './ListingCardSoldOverlay'
import { listingPublicPath } from '../utils/listingPath'

interface ListingImage {
  id: number
  thumbnail_url: string
  is_primary: boolean
}

interface Listing {
  id: number
  title: string
  price: number
  condition: string
  battery_health?: number
  owner_id: number
  is_sold?: boolean
  phone_model: { manufacturer: string; model_name: string; hero_image_path?: string | null; release_year?: number | null }
  images?: ListingImage[]
}

interface FavoritesPageProps {
  token: string
  currentUserId?: number
  onBuyNow?: (listingId: number) => void
  onOpenListingDetail?: (listingId: number) => void
  onBrowse?: () => void
}

export default function FavoritesPage({
  token,
  currentUserId,
  onBuyNow,
  onOpenListingDetail,
  onBrowse,
}: FavoritesPageProps) {
  const { t, i18n } = useTranslation()
  const { ids, toggle, has, reconcileIds } = useFavorites()
  const [listings, setListings] = useState<Listing[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null)

  useEffect(() => {
    let cancelled = false
    if (ids.length === 0) {
      setListings([])
      setLoading(false)
      return
    }
    setLoading(true)
    Promise.all(
      ids.map((id) =>
        listingsAPI
          .getListing(id)
          .then((res) => res.data as Listing)
          .catch(() => null),
      ),
    )
      .then((rows) => {
        if (cancelled) return
        const valid = rows.filter((r): r is Listing => r != null)
        reconcileIds(valid.map((l) => l.id))
        setListings(valid)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [ids, reconcileIds])

  function getConditionColor(condition: string): string {
    return conditionColor(condition)
  }

  const isOwnListing = (listing: Listing) =>
    currentUserId !== undefined && listing.owner_id === currentUserId
  const isOwnBlocked = (listing: Listing) => blocksOwnListingAction(listing.owner_id, currentUserId)

  function openDetail(listingId: number) {
    onOpenListingDetail?.(listingId)
  }

  const isEmpty = ids.length === 0

  return (
    <section className="favorites-page favorites-page--modern">
      {!isEmpty && (
        <header className="favorites-page__hero">
          <div className="favorites-page__hero-glow" aria-hidden="true" />
          <div className="favorites-page__hero-icon" aria-hidden="true">
            <IconHeart />
          </div>
          <h2>{t('vc.favorites.title')}</h2>
          <p className="favorites-page__hint">{t('vc.favorites.hint')}</p>
        </header>
      )}

      {isEmpty && loading && (
        <div className="favorites-empty favorites-empty--bloom favorites-empty--loading">
          <div className="favorites-empty__glow" aria-hidden="true" />
          <div className="favorites-empty__icon" aria-hidden="true">
            <IconHeart />
          </div>
          <p className="favorites-empty__title">{t('common.loading')}</p>
        </div>
      )}

      {isEmpty && !loading && (
        <div className="favorites-empty favorites-empty--bloom">
          <div className="favorites-empty__glow" aria-hidden="true" />
          <div className="favorites-empty__icon" aria-hidden="true">
            <IconHeart />
          </div>
          <p className="favorites-empty__title">{t('vc.favorites.emptyTitle')}</p>
          <p className="favorites-empty__hint">{t('vc.favorites.empty')}</p>
          {onBrowse ? (
            <button type="button" className="favorites-empty__cta" onClick={onBrowse}>
              {t('vc.favorites.browseCta')}
            </button>
          ) : null}
        </div>
      )}

      {!isEmpty && (
        <div className="favorites-page__body">
          {loading && listings.length === 0 && (
            <p className="favorites-page__loading">{t('common.loading')}</p>
          )}
          <div className="listings-grid">
            {listings.map((listing) => {
              const cardPhotos = listingCardPhotoUrls(listing)
              return (
              <article
                key={listing.id}
                className={`listing-card listing-card--clickable${listing.is_sold ? ' listing-card--sold' : ''}`}
              >
            <div
              className={listingCardSoldCoverClass(
                `listing-card-cover listing-card-hit${cardPhotos.isUserPhoto ? ' listing-card-cover--user' : ''}`,
                listing.is_sold,
              )}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  openDetail(listing.id)
                }
              }}
            >
              <ListingCardPhotoCarousel
                urls={cardPhotos.urls}
                isUserPhoto={cardPhotos.isUserPhoto}
                onTap={() => openDetail(listing.id)}
              />
              <ListingCardSoldOverlay show={listing.is_sold} />
              <ListingFavoriteButton
                active={has(listing.id)}
                label={t('vc.favorites.remove')}
                onClick={(e) => {
                  e.stopPropagation()
                  toggle(listing.id)
                }}
              />
              {isOwnListing(listing) && <span className="listing-own-badge">{t('catalog.yourListing')}</span>}
            </div>
            <div className="listing-card-body">
              <a
                className="listing-card-hit"
                href={listingPublicPath(listing.id, i18n.language)}
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
                  e.preventDefault()
                  openDetail(listing.id)
                }}
              >
              <h3>{listing.phone_model.manufacturer}</h3>
              <p className="model-name">{listing.phone_model.model_name}</p>
              <p className="title">{listing.title}</p>
              <div className="listing-card-meta">
                <p className="price">€{listing.price.toFixed(2)}</p>
                <div className="listing-card-badges">
                  <span
                    className="condition-badge"
                    style={{ backgroundColor: getConditionColor(listing.condition) }}
                  >
                    {formatConditionLabel(listing.condition, t)}
                  </span>
                  {listing.battery_health != null && listing.battery_health > 0 && (
                    <span className="battery">
                      <span className="battery-mark" aria-hidden="true">⏻</span>
                      {listing.battery_health}%
                    </span>
                  )}
                </div>
              </div>
              </a>
              {listing.is_sold ? (
                <p className="listing-own-hint">{t('listing.detailSold')}</p>
              ) : isOwnBlocked(listing) ? (
                <p className="listing-own-hint">{t('catalog.cannotOfferOwn')}</p>
              ) : (
                <div className="listing-card-actions listing-card-actions--simple">
                  <button
                    type="button"
                    className="listing-card-btn listing-card-btn--buy listing-card-btn--buy-hero"
                    onClick={() => onOpenListingDetail?.(listing.id)}
                  >
                    {t('catalog.viewListing')}
                  </button>
                  <button
                    type="button"
                    className="listing-card-btn listing-card-btn--ghost"
                    onClick={() => setSelectedListing(listing)}
                  >
                    {t('catalog.offerShort')}
                  </button>
                </div>
              )}
            </div>
          </article>
            )
            })}
          </div>
        </div>
      )}

      {selectedListing && (
        <OfferModal
          listingId={selectedListing.id}
          currentPrice={selectedListing.price}
          token={token}
          isOpen={Boolean(selectedListing)}
          onClose={() => setSelectedListing(null)}
          overlayClassName="modal-overlay--stack-top"
        />
      )}
    </section>
  )
}
